import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StorefrontIcon from "@mui/icons-material/Storefront";

export type PromotionPhase = "form" | "submitting" | "success";

export interface PromoteToResellerModalDelegate {
  onConfirm: () => void;
  onClose: () => void;
}

interface Props {
  isOpen: boolean;
  phase: PromotionPhase;
  errorMessage?: string;
  userName: string;
  delegate: PromoteToResellerModalDelegate;
}

export function PromoteToResellerModal({
  isOpen,
  phase,
  errorMessage,
  userName,
  delegate,
}: Props) {
  const renderSubmitting = () => (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      gap={2}
      paddingY={5}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Promovendo usuário...
      </Typography>
    </Box>
  );

  const renderSuccess = () => (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      gap={1}
      paddingY={5}
    >
      <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
      <Typography variant="subtitle1" color="success.main" fontWeight="bold">
        Usuário promovido!
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center">
        <strong>{userName}</strong> agora tem o perfil de Revendedor.
      </Typography>
    </Box>
  );

  const renderForm = () => (
    <Box display="flex" flexDirection="column" sx={{ gap: 2 }}>
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Typography color="text.secondary">
        Tem certeza de que deseja promover o usuário <strong>{userName}</strong>{" "}
        para o perfil de Revendedor?
      </Typography>

      <Alert severity="info">
        Ele ganhará acesso ao painel de gerenciamento de clientes e distribuição
        de licenças. Esta ação não pode ser facilmente desfeita.
      </Alert>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        if (phase === "submitting") {
          return;
        }
        delegate.onClose();
      }}
      variant="modal"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle variant="modal">
        <StorefrontIcon />
        Promover para Revendedor
      </DialogTitle>
      <DialogContent variant="modal">
        {phase === "submitting" && renderSubmitting()}
        {phase === "success" && renderSuccess()}
        {phase === "form" && renderForm()}
      </DialogContent>
      {phase === "form" && (
        <DialogActions
          variant="modal"
          sx={{ flexWrap: "wrap", gap: 1, justifyContent: "end" }}
        >
          <Button variant="outlined" color="error" onClick={delegate.onClose}>
            Cancelar
          </Button>

          <Button variant="contained" onClick={delegate.onConfirm}>
            Confirmar Promoção
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
